/**
 * Shrinks a PDF in the browser before it is uploaded.
 *
 * Why in the browser rather than on the server: the size limit is not the only
 * problem a 40 MB scan causes. Sending it at all is slow on an office
 * connection, and it then has to come back down again for anything that reads
 * it. Compressing first fixes the upload as well as the limit.
 *
 * Two stages, in order, stopping as soon as the file is small enough:
 *
 *   1. Re-save with pdf-lib. Rewrites the document without the redundancy most
 *      generators leave behind, and with object streams on. Keeps the text
 *      layer intact, so the result is still selectable and searchable. Usually
 *      wins 10-30%, which is enough for a merely awkward file.
 *
 *   2. Rasterise at reduced resolution. This is the one that actually rescues a
 *      40 MB statement, because those are almost always photographed pages
 *      embedded at full camera resolution. Each page is drawn to a canvas and
 *      re-encoded as JPEG, then reassembled into a PDF.
 *
 * Stage 2 replaces the page with an image, so the text layer goes. That is a
 * real cost - the document stops being selectable or searchable - and it is why
 * it only runs when stage 1 has not got the file under the limit.
 *
 * The libraries load on demand. They are large, and a rep uploading a 200 KB
 * voided check should not pay for them.
 */

const MB = 1024 * 1024;

/** Files at or below this are uploaded untouched. */
export const COMPRESS_THRESHOLD_BYTES = 8 * MB;

/** What stage 2 aims for. Comfortably under every limit in the app. */
const TARGET_BYTES = 7 * MB;

// Tried in order. Lower scale and quality each time, so an enormous file keeps
// shrinking rather than giving up after one attempt.
const RASTER_ATTEMPTS = [
  { scale: 1.5,  quality: 0.75 },
  { scale: 1.15, quality: 0.65 },
  { scale: 0.9,  quality: 0.55 },
  { scale: 0.7,  quality: 0.45 },
];

let libs = null;
async function loadLibs() {
  if (libs) return libs;
  const [pdfLib, pdfjs] = await Promise.all([
    import('pdf-lib'),
    import('pdfjs-dist/build/pdf'),
  ]);
  try {
    const worker = await import('pdfjs-dist/build/pdf.worker.entry');
    pdfjs.GlobalWorkerOptions.workerSrc = worker.default || worker;
  } catch {
    // Without a worker pdfjs still runs, just on the main thread. Slower, but a
    // working upload beats a failed one.
  }
  libs = { pdfLib, pdfjs };
  return libs;
}

const isPdf = file =>
  file && (file.type === 'application/pdf' || /\.pdf$/i.test(file.name || ''));

/** A File carrying the same name, so nothing downstream has to care. */
function asFile(bytes, original) {
  return new File([bytes], original.name, {
    type: 'application/pdf',
    lastModified: original.lastModified || Date.now(),
  });
}

/** Stage 1 - rewrite the document, keeping the text. */
async function reSave(bytes) {
  const { pdfLib } = await loadLibs();
  const doc = await pdfLib.PDFDocument.load(bytes, { ignoreEncryption: true });
  return doc.save({ useObjectStreams: true });
}

/** Stage 2 - draw each page and re-encode it as JPEG. */
async function rasterise(bytes, { scale, quality }) {
  const { pdfLib, pdfjs } = await loadLibs();
  // A copy, because pdfjs takes ownership of the buffer it is handed.
  const src = await pdfjs.getDocument({ data: bytes.slice(0) }).promise;
  const out = await pdfLib.PDFDocument.create();

  for (let n = 1; n <= src.numPages; n++) {
    const page = await src.getPage(n);
    const viewport = page.getViewport({ scale });
    const canvas = document.createElement('canvas');
    canvas.width = Math.max(1, Math.floor(viewport.width));
    canvas.height = Math.max(1, Math.floor(viewport.height));
    const ctx = canvas.getContext('2d');
    // Scans are photographs of white paper; without this, any transparent
    // region renders black.
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    await page.render({ canvasContext: ctx, viewport }).promise;

    const dataUrl = canvas.toDataURL('image/jpeg', quality);
    const img = await out.embedJpg(dataUrl);
    const p = out.addPage([img.width, img.height]);
    p.drawImage(img, { x: 0, y: 0, width: img.width, height: img.height });

    // Let the browser breathe between pages so the tab stays responsive on a
    // long statement.
    await new Promise(r => setTimeout(r, 0));
  }
  try { await src.destroy(); } catch {}
  return out.save({ useObjectStreams: true });
}

/**
 * Compress `file` if it needs it.
 *
 * onProgress(stage, detail) is called as it goes, so the caller can say what is
 * happening rather than showing a stalled bar.
 *
 * Never throws for a file it cannot handle: an encrypted or malformed PDF comes
 * back untouched and is uploaded as-is, which is what would have happened
 * anyway. Failing the upload because compression failed would be worse than the
 * problem being solved.
 */
export async function compressPdfIfNeeded(file, onProgress = () => {}) {
  if (!isPdf(file) || file.size <= COMPRESS_THRESHOLD_BYTES) {
    return { file, compressed: false, originalSize: file?.size ?? 0, finalSize: file?.size ?? 0 };
  }

  const originalSize = file.size;
  let bytes;
  try {
    bytes = new Uint8Array(await file.arrayBuffer());
  } catch {
    return { file, compressed: false, originalSize, finalSize: originalSize };
  }

  // Stage 1
  try {
    onProgress('resaving', { originalSize });
    const saved = await reSave(bytes);
    if (saved.length < originalSize) bytes = saved;
    if (bytes.length <= TARGET_BYTES) {
      return { file: asFile(bytes, file), compressed: bytes.length < originalSize, originalSize, finalSize: bytes.length, method: 'resave' };
    }
  } catch {
    // Encrypted, or something pdf-lib will not open. Fall through: rasterising
    // may still work, and if it does not the original is uploaded.
  }

  // Stage 2
  for (const attempt of RASTER_ATTEMPTS) {
    try {
      onProgress('rasterising', { originalSize, attempt });
      const out = await rasterise(bytes, attempt);
      if (out.length <= TARGET_BYTES) {
        return { file: asFile(out, file), compressed: true, originalSize, finalSize: out.length, method: 'raster', attempt };
      }
      // Keep the smallest thing seen so far and try harder.
      if (out.length < bytes.length) bytes = out;
    } catch {
      break;   // pdfjs could not render it; stop rather than loop on a failure
    }
  }

  const finalSize = bytes.length;
  return {
    file: finalSize < originalSize ? asFile(bytes, file) : file,
    compressed: finalSize < originalSize,
    originalSize,
    finalSize,
    method: 'raster',
    // The caller can still upload this; it is simply as small as we could make
    // it, which may still be over a downstream limit.
    underTarget: finalSize <= TARGET_BYTES,
  };
}

export function formatSize(bytes) {
  if (!bytes && bytes !== 0) return '';
  return bytes >= MB ? (bytes / MB).toFixed(1) + ' MB' : Math.round(bytes / 1024) + ' KB';
}
